import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { apiClient } from "../../base_url/config"

// Request interface
export interface CreateOrderRequest {
    cartId: number
    platform: "qr"
    typeId: number
    notes: string
    paymentMethod: "online"
    branchId: number
    phone: string
    coboneCode?: string
    tableId?: number
    tableTypeId?: number
    tablePrice?: number
}

// Order data interface
export interface OrderData {
    id: number
    type_id: number
    branch_id: number
    platform: string
    notes: string | null
    payment_method: string
    address_id: number | null
    created_at: string
    updated_at: string
    payment_status: string
    status_id: string
    total_price: string
    user_id: number
}

// Response interface
export interface CreateOrderResponse {
    message: string
    descriptiveMessage: string | null
    data: {
        order: OrderData
    }
}

// State interface
interface CreateOrderState {
    isLoading: boolean
    error: string | null
    orderData: OrderData | null
    lastOrderId: number | null
}

const initialState: CreateOrderState = {
    isLoading: false,
    error: null,
    orderData: null,
    lastOrderId: null,
}

// Async thunk for creating order
export const createOrder = createAsyncThunk(
    "createOrder/createOrder",
    async (
        { orderData, token, lang }: {
            orderData: CreateOrderRequest
            token: string
            lang: string
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await apiClient.post<CreateOrderResponse>(
                "/qr/orders",
                orderData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        // lang will be added automatically by interceptor
                    },
                }
            )

            return {
                orderData: response.data.data.order,
                message: response.data.message,
                orderId: response.data.data.order.id,
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "Order creation failed"
            return rejectWithValue(errorMessage)
        }
    }
)

const createOrderSlice = createSlice({
    name: "createOrder",
    initialState,
    reducers: {
        clearOrderError: (state) => {
            state.error = null
        },
        clearOrderData: (state) => {
            state.orderData = null
            state.lastOrderId = null
            state.error = null
        },
        resetOrderState: (state) => {
            state.isLoading = false
            state.error = null
            state.orderData = null
            state.lastOrderId = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createOrder.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.orderData = action.payload.orderData
                state.lastOrderId = action.payload.orderId
                state.error = null
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.orderData = null
            })
    },
})

export const { clearOrderError, clearOrderData, resetOrderState } = createOrderSlice.actions

// Selectors
export const selectCreateOrder = (state: { createOrder: CreateOrderState }) => state.createOrder
export const selectOrderLoading = (state: { createOrder: CreateOrderState }) => state.createOrder.isLoading
export const selectOrderError = (state: { createOrder: CreateOrderState }) => state.createOrder.error
export const selectOrderData = (state: { createOrder: CreateOrderState }) => state.createOrder.orderData
export const selectLastOrderId = (state: { createOrder: CreateOrderState }) => state.createOrder.lastOrderId

export default createOrderSlice.reducer